import { useEffect, useState } from 'react'

import {
  Button,
  OTPInput,
  ProgressCircularLoader,
  Snackbar
} from '@paysense-india-services/elevate-web'

import { OTP_LENGTH, PROGRESS_COMPLETE, TIMER_INTERVAL_MS } from '@/components/OtpEntry/constants'

const BRAND_COLOR = '#0ABF89'

const formatTimer = seconds => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const TimerDisplay = ({ progress, timer }) => (
  <div className="ew-typography-small ew-fw-regular ew-d-flex ew-pt-24">
    <div className="ew-mr-8">
      <ProgressCircularLoader color={BRAND_COLOR} progress={progress} size={20} strokeWidth={2} />
    </div>
    <div className="ew-color-secondary">Waiting for OTP...</div>
    <div className="ew-color-primary">{formatTimer(timer)}</div>
  </div>
)

const ResendButton = ({ isDisabled, isLoading, onResend }) => (
  <div className="ew-mb-24">
    <Button disabled={isDisabled} onClick={onResend} size="medium" type="link" variant="brand">
      {isLoading ? 'Sending...' : 'Resend OTP'}
    </Button>
  </div>
)

const OtpEntry = ({
  title,
  subtitle,
  onVerify,
  verifyButtonText,
  verifyLoading,
  incompleteOtpMessage,
  onCancel,
  cancelButtonText = 'Cancel',
  onResend,
  isResendLoading = false,
  resendAfterSeconds: resendAfterSecondsProp = 0
}) => {
  const resendAfterSeconds = Math.max(0, Math.floor(Number(resendAfterSecondsProp) || 0))

  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [timer, setTimer] = useState(resendAfterSeconds)
  const [isWaiting, setIsWaiting] = useState(resendAfterSeconds > 0)

  // When parent passes new delay (e.g. after resend), restart timer
  useEffect(() => {
    if (resendAfterSeconds > 0) {
      setTimer(resendAfterSeconds)
      setIsWaiting(true)
    } else {
      setIsWaiting(false)
    }
  }, [resendAfterSeconds])

  // Single stable interval: do not depend on `timer` or the effect resets every tick
  useEffect(() => {
    if (!isWaiting || resendAfterSeconds <= 0) return undefined
    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          setIsWaiting(false)
          return 0
        }
        return prev - 1
      })
    }, TIMER_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isWaiting, resendAfterSeconds])

  const progress =
    resendAfterSeconds > 0
      ? ((resendAfterSeconds - timer) / resendAfterSeconds) * PROGRESS_COMPLETE
      : PROGRESS_COMPLETE

  const showTimer = resendAfterSeconds > 0 && isWaiting && timer > 0

  const handleChange = value => {
    setOtp(value)
    if (error) setError('')
  }

  const isComplete = otp?.length === OTP_LENGTH

  const handleVerifyClick = () => {
    if (!isComplete) {
      setError(incompleteOtpMessage)
      Snackbar.show(incompleteOtpMessage, { type: 'error' })
      return
    }
    onVerify(otp, setError)
  }

  const showResend = onResend && !showTimer

  let resendOrTimer = null
  if (showTimer) {
    resendOrTimer = <TimerDisplay progress={progress} timer={timer} />
  } else if (showResend) {
    resendOrTimer = (
      <ResendButton
        isDisabled={verifyLoading || isResendLoading}
        isLoading={isResendLoading}
        onResend={onResend}
      />
    )
  }

  return (
    <>
      <h1 className="ew-typography-large ew-fw-bold ew-color-primary">{title}</h1>
      <p className="ew-typography-normal ew-color-secondary ew-fw-regular ew-mb-24">{subtitle}</p>
      <div className="ew-w-100 ew-mb-24">
        <OTPInput
          autoFocusInit
          error={error}
          onChange={handleChange}
          otp={otp}
          otpLength={OTP_LENGTH}
        />
      </div>

      {resendOrTimer}

      <div className={onCancel ? 'ew-d-flex ew-gap-12 ew-mt-24' : 'ew-mt-24'}>
        {onCancel ? (
          <Button
            disabled={verifyLoading}
            onClick={onCancel}
            size="medium"
            type="default"
            variant="secondary"
          >
            {cancelButtonText}
          </Button>
        ) : null}
        <Button
          className={onCancel ? 'ew-flex-1' : ''}
          fullWidth={!onCancel}
          isDisabled={verifyLoading || !isComplete || !!error}
          isLoading={verifyLoading}
          onClick={handleVerifyClick}
          size="medium"
          type="default"
          variant="brand"
        >
          {verifyButtonText}
        </Button>
      </div>
      <Snackbar />
    </>
  )
}

export default OtpEntry
